// NOTE: The form logic is reused from FacultyLabInternalsPage.jsx (see comments below)
// This component is standalone and can be used for lab creation.

import React, { useState, useEffect } from "react";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import axiosInstance from "../../services/axiosInstance";
import { getActiveQuestions } from "../../services/questionBank.api";
import {
  setFacultyAllowedQuestions,
  getFacultyLabInternalDetails,
  getFacultyAllowedQuestions,
  getFacultyEvaluationSetKeys
} from "../../services/labInternals.api";
import { useNavigate, useParams } from "react-router-dom";
import AllowedQuestionsSection from "../../components/AllowedQuestionsSection";
import { IST_TIME_ZONE } from "../../utils/istDateTime";

function toDatetimeLocalIST(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(date);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}

function toISTOffsetDateTime(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    return s;
  }
  return `${s}:00+05:30`;
}

const CreateLabInternalPage = () => {
  const navigate = useNavigate();
  const { labId } = useParams();
  const isEditMode = Boolean(labId);
  const [form, setForm] = useState({
    title: "",
    // course: "",
    course: "CS621PE", // Hardcoded course value
    description: "",
    startDate: "",
    endDate: "",
    status: "",
    part1MaxMarks: 50, // Default value
    part2MaxMarks: 50, // Default value
    evaluationType: "AUTO", // Default value
  });
  const [allowedQuestions, setAllowedQuestions] = useState([]); // [{question, part1Marks, part2Marks}]
  const [initialAllowedQuestions, setInitialAllowedQuestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [evaluationSetKeys, setEvaluationSetKeys] = useState([]);

  useEffect(() => {
    getFacultyEvaluationSetKeys()
      .then(setEvaluationSetKeys)
      .catch(() => setEvaluationSetKeys([]));
  }, []);

  // Questions
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionsError, setQuestionsError] = useState("");

  // For new record, show all questions, but do not pre-select any
  useEffect(() => {
    if (isEditMode) return;
    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      setQuestionsError("");
      try {
        const data = await getActiveQuestions();
        const sorted = Array.isArray(data)
          ? data.sort((a, b) => {
              const numA = parseInt(a.questionId.replace(/^Q/, ""), 10);
              const numB = parseInt(b.questionId.replace(/^Q/, ""), 10);
              return numA - numB;
            })
          : [];
        setQuestions(sorted);
        setAllowedQuestions([]); // No pre-selection for new record
      } catch (err) {
        setQuestionsError("Unable to load questions.");
      } finally {
        setLoadingQuestions(false);
      }
    };
    fetchQuestions();
  }, [isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Prefill form and allowedQuestions if editing
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const labRes = await getFacultyLabInternalDetails(labId);
        const lab = labRes.lab || labRes;
        // console.log('lab')
        setForm({
          title: lab.title || "",
          course: lab.courseId || "CS621PE",
          description: lab.domain || "",
          startDate: toDatetimeLocalIST(lab.startDate),
          endDate: toDatetimeLocalIST(lab.endDate),
          status: lab.status,
          part1MaxMarks: lab.part1MaxMarks || 50,
          part2MaxMarks: lab.part2MaxMarks || 50,
          evaluationType: lab.evaluationType || "AUTO",
        });
        // Prefill allowedQuestions
        const allowedQ = await getFacultyAllowedQuestions(labId);
        const allowedRaw =
          allowedQ?.data?.allowedQuestions ??
          allowedQ?.allowedQuestions ??
          [];
        if (Array.isArray(allowedRaw)) {
          const mappedAllowedQuestions = allowedRaw.map((q) => ({
            questionId: q.questionId?._id || q.questionId,
            setKey: q.setKey ?? "",
            part1MaxMarks: q.part1MaxMarks,
            part2MaxMarks: q.part2MaxMarks
          }));
          setAllowedQuestions(mappedAllowedQuestions);
          setInitialAllowedQuestions(mappedAllowedQuestions);
        }
        const questionsData = await getActiveQuestions();
        const sorted = Array.isArray(questionsData)
          ? questionsData.sort((a, b) => {
              const numA = parseInt(a.questionId.replace(/^Q/, ""), 10);
              const numB = parseInt(b.questionId.replace(/^Q/, ""), 10);
              return numA - numB;
            })
          : [];
        setQuestions(sorted);
        // console.log("In EditMode useEffect")
      } catch (e) {
        setSnackbar({ open: true, message: 'Failed to load lab for editing', severity: 'error' });
      }
      finally{
        setLoadingQuestions(false);
      }
    })();
  }, [isEditMode, labId]);
  // console.log('Allowed Questions', allowedQuestions)

  const serializeAllowedQuestions = (items) =>
    (Array.isArray(items) ? items : [])
      .map((q) => ({
        questionId: String(q.questionId || ""),
        setKey: String(q.setKey || "").trim(),
        part1MaxMarks: Number(q.part1MaxMarks),
        part2MaxMarks: Number(q.part2MaxMarks)
      }))
      .sort((a, b) => {
        const byQuestion = a.questionId.localeCompare(b.questionId);
        if (byQuestion !== 0) return byQuestion;
        return a.setKey.localeCompare(b.setKey);
      });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);
    // Validate marks
    for (const q of allowedQuestions) {
      if (
        !q.setKey ||
        String(q.setKey).trim() === "" ||
        !q.part1MaxMarks ||
        isNaN(q.part1MaxMarks) ||
        !q.part2MaxMarks ||
        isNaN(q.part2MaxMarks)
      ) {
        setSubmitError(
          "Each selected question needs an evaluation set (setKey) and valid Part 1 / Part 2 marks.",
        );
        setSubmitting(false);
        return;
      }
    }
    try {
      let id = labId;
      let result = null;
      if (isEditMode) {
        // Update lab
        result = await axiosInstance.put(`/lab-internals/faculty/${labId}`, {
          ...form,
          startDate: toISTOffsetDateTime(form.startDate),
          endDate: toISTOffsetDateTime(form.endDate),
          part1MaxMarks: Number(form.part1MaxMarks),
          part2MaxMarks: Number(form.part2MaxMarks),
          evaluationType: form.evaluationType,
          // allowedQuestions: allowedQuestions
        });
      } else {
        // Create lab
        console.log('Creating lab with status:', form.status, form);
        const labRes = await axiosInstance.post("/lab-internals/faculty", {
          ...form,
          startDate: toISTOffsetDateTime(form.startDate),
          endDate: toISTOffsetDateTime(form.endDate),
          status: form.status,
          part1MaxMarks: Number(form.part1MaxMarks),
          part2MaxMarks: Number(form.part2MaxMarks),
          evaluationType: form.evaluationType,
          // allowedQuestions: allowedQuestions
        });
        id = labRes.data?.data?._id || labRes.data?.data?.labId;
        result = labRes?.success;
        console.log('Created lab ID:', labRes, 'Extracted ID:', id, result);
        if (!id) throw new Error("Lab creation failed");
      }
      const allowedQuestionsChanged =
        JSON.stringify(serializeAllowedQuestions(initialAllowedQuestions)) !==
        JSON.stringify(serializeAllowedQuestions(allowedQuestions));

      // Step 2: Update allowed questions only when they changed.
      if (!isEditMode || allowedQuestionsChanged) {
        console.log('Setting allowed questions:', id, allowedQuestions);
        await setFacultyAllowedQuestions(id, allowedQuestions);
      }
    
      setSubmitSuccess(true);
      setForm({
        title: "",
        course: "CS621PE",
        description: "",
        startDate: "",
        endDate: "",
        status: form.status || "DRAFT",
      });
      setAllowedQuestions([]);
      setSnackbar({ open: true, message: isEditMode ? 'Lab updated successfully!' : 'Lab created successfully!', severity: 'success' });
      setTimeout(() => {
        navigate('/faculty/lab-internals'); // Dashboard route
      }, 1200);
    } catch (err) {
      setSubmitError(isEditMode ? "Failed to update lab. Please try again." : "Failed to create lab. Please try again.");
      setSnackbar({ open: true, message: isEditMode ? 'Failed to update lab. Please try again.' : 'Failed to create lab. Please try again.', severity: 'error' });
      console.log('Error',err)
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
       {isEditMode ? "Edit Lab Internal" : "Create Lab Internal"}
      </Typography>
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Lab Title"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              fullWidth
            />
            
            {/* <TextField
              label="Course"
              name="course"
              value={form.course}
              onChange={handleChange}
              required
              fullWidth
            /> */}
           
            <TextField
              label="Course"
              name="course"
              value={form.course}
              disabled
              fullWidth
              helperText="Course is hardcoded to CS621PE"
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              required
              multiline
              minRows={2}
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 2 }}>

            <TextField
              label="Start Date"
              name="startDate"
              type="datetime-local"
              value={form.startDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
              />
            <TextField
              label="End Date"
              name="endDate"
              type="datetime-local"
              value={form.endDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
              />
              </Box>
            <TextField
              label="Status"
              name="status"
              select
              value={form.status}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </TextField>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Part 1 Max Marks"
                name="part1MaxMarks"
                type="number"
                value={form.part1MaxMarks}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Part 2 Max Marks"
                name="part2MaxMarks"
                type="number"
                value={form.part2MaxMarks}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Evaluation Type"
                name="evaluationType"
                select
                value={form.evaluationType}
                onChange={handleChange}
                required
                fullWidth
              >
                <MenuItem value="AUTO">Auto</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
              </TextField>
            </Box>

            {/* Configure Questions Section */}
            <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Configure Questions for this Lab
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {allowedQuestions.length} selected
              </Typography>
            </Box>
            {loadingQuestions ? (
              <CircularProgress size={24} />
            ) : questionsError ? (
              <Typography color="error">{questionsError}</Typography>
            ) : (
              <AllowedQuestionsSection
                value={allowedQuestions}
                onChange={setAllowedQuestions}
                disabled={submitting}
                showHeader={true}
                questionsProp={questions}
                evaluationSetKeys={evaluationSetKeys}
              />
            )}
            {submitError && (
              <Typography color="error">{submitError}</Typography>
            )}
            {submitSuccess && (
              <Typography color="success.main">
                Lab created successfully!
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={20} />}
              >
                {isEditMode ? "Update Lab" : "Create Lab"}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                disabled={submitting}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    <Snackbar
      open={snackbar.open}
      autoHideDuration={2000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
        {snackbar.message}
      </MuiAlert>
    </Snackbar>
    </Box>
  );
};

export default CreateLabInternalPage;
